import { useCallback, useMemo, useState } from 'react';

import { useResizableColumns, _ACTIONS_OVERHEAD } from './useResizableColumns';
import type { ColumnDef } from 'features/common/components/Punchup/composites/tables/PunchupColumnPicker/types';

export type { ColumnDef };

export interface UseColumnSelectionReturn {
	allDefs: ColumnDef[];
	visibleDefs: ColumnDef[];
	visibleKeys: Set<string>;
	/** CSS grid-template-columns string derived from visible columns + optional fixed actions column */
	gridColumns: string;
	/** Pixel widths for columns the user has explicitly resized. */
	columnWidths: Map<string, number>;
	isVisible: (key: string) => boolean;
	toggleColumn: (key: string) => void;
	/** Update a single column's pixel width (called from header resize-handle drag). */
	setColumnWidth: (key: string, width: number) => void;
}

/**
 * Manages which columns are visible in a Punchup table and their pixel widths.
 * Columns flagged with `defaultHidden: true` start hidden; everything else is
 * visible by default. Users toggle via `PunchupColumnPicker`. Resize logic is
 * delegated to `useResizableColumns`.
 *
 * Default column track is `minmax(max-content, 1fr)` — auto-fit content with
 * `1fr` slack distribution when the table fits the page; collapses to pure
 * `max-content` when the table overflows. See PunchupTable CLAUDE.md →
 * "Column width strategy" for the duality and the diagnostic for "why is my
 * column too wide?".
 *
 * @param defs - Column definitions in their original (priority-aware) order.
 * @param hasActionsColumn - Pass `false` for tables that have no sticky actions
 *   column. When `true` (default), a fixed `80px` actions track is appended to
 *   the grid template. When `false`, no actions track is appended; callers
 *   must not render an actions cell.
 */
export function useColumnSelection(defs: ColumnDef[], hasActionsColumn = true): UseColumnSelectionReturn {
	// Columns are visible by default unless individually flagged with
	// `defaultHidden: true` on their ColumnDef. Users can re-enable hidden
	// columns via PunchupColumnPicker.
	const [visibleKeys, setVisibleKeys] = useState<Set<string>>(
		() => new Set(defs.filter((d) => !d.defaultHidden).map((d) => d.key))
	);

	const visibleDefs = useMemo(
		() => defs.filter((col) => visibleKeys.has(col.key)).sort((a, b) => a.priority - b.priority),
		[defs, visibleKeys]
	);

	const visibleColumnKeys = useMemo(() => visibleDefs.map((d) => d.key), [visibleDefs]);

	const minWidthByKey = useMemo(
		() => new Map(defs.filter((d) => d.minWidth !== undefined).map((d) => [d.key, d.minWidth as number])),
		[defs]
	);

	const { gridColumns, columnWidths, setColumnWidth } = useResizableColumns(visibleColumnKeys, {
		hasActionsColumn,
		minWidthByKey,
	});

	const isVisible = useCallback((key: string) => visibleKeys.has(key), [visibleKeys]);

	const toggleColumn = useCallback((key: string) => {
		setVisibleKeys((prev) => {
			const next = new Set(prev);
			if (next.has(key)) {
				// Never hide the last visible column
				if (next.size <= 1) return prev;
				next.delete(key);
			} else {
				next.add(key);
			}
			return next;
		});
	}, []);

	return {
		allDefs: defs,
		visibleDefs,
		visibleKeys,
		gridColumns,
		columnWidths,
		isVisible,
		toggleColumn,
		setColumnWidth,
	};
}

/** @internal Exported only for tests — do not use in production code. */
export { _ACTIONS_OVERHEAD };
